import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, Cpu, QrCode } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Format a numeric string of cents into "1.234,56"
function formatCents(cents) {
  const num = (Number(cents) || 0) / 100;
  return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function AmountForm({ onCreated }) {
  const [params] = useSearchParams();
  const presetAmount = useMemo(() => {
    const v = parseFloat(params.get("amount") || "");
    return Number.isFinite(v) && v > 0 ? Math.round(v * 100) : null;
  }, [params]);
  const lockAmount = presetAmount !== null;

  const [cents, setCents] = useState(presetAmount ?? 100); // default R$ 1,00
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (presetAmount !== null) setCents(presetAmount);
  }, [presetAmount]);

  const display = useMemo(() => formatCents(cents), [cents]);
  const valueBRL = useMemo(() => Number((cents / 100).toFixed(2)), [cents]);

  const handleKeyInput = (e) => {
    if (lockAmount) return; // amount preset via ?amount= query param
    // Allow numeric / backspace; build cents
    if (e.key === "Backspace") {
      setCents((c) => Math.floor(c / 10));
      return;
    }
    if (/^\d$/.test(e.key)) {
      setCents((c) => {
        const next = c * 10 + parseInt(e.key, 10);
        return Math.min(next, 99999999); // up to R$ 999.999,99
      });
    }
  };

  const handleQuick = (val) => {
    if (lockAmount) return;
    setCents(Math.round(val * 100));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (valueBRL <= 0) {
      toast.error("Informe um valor maior que R$ 0,00");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/create`, { amount: valueBRL });
      toast.success("PIX gerado com sucesso!");
      onCreated(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Falha ao gerar PIX. Tente novamente.";
      toast.error(typeof msg === "string" ? msg : "Falha ao gerar PIX");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="neon-card rounded-[28px] p-6 sm:p-8 rise rise-1" data-testid="amount-form">
      <form onSubmit={submit} className="space-y-7">
        <div>
          <label
            htmlFor="amount-input"
            className="text-xs tracking-[0.22em] uppercase text-[var(--muted)] mono mb-3 block"
          >
            Valor a pagar
          </label>

          <div
            tabIndex={0}
            onKeyDown={handleKeyInput}
            className="neon-input rounded-2xl px-5 py-5 sm:py-6 flex items-baseline gap-3 cursor-text select-none focus:outline-none"
            data-testid="amount-input-wrapper"
          >
            <span className="text-[var(--neon)] mono text-lg sm:text-xl font-medium opacity-90">
              R$
            </span>
            <span
              id="amount-input"
              className="mono text-3xl sm:text-5xl font-bold tracking-tight text-white tabular-nums flex-1"
              data-testid="amount-display"
            >
              {display}
            </span>
            <span className="mono text-[10px] tracking-[0.24em] uppercase text-[var(--muted)] hidden sm:inline">
              {lockAmount ? "FIXO" : "BRL"}
            </span>
          </div>

          {!lockAmount && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="quick-values">
              {[1, 5, 10, 25, 50, 100].map((v) => (
                <button
                  type="button"
                  key={v}
                  onClick={() => handleQuick(v)}
                  className="neon-ghost-btn rounded-full px-3.5 py-1.5 text-xs mono uppercase tracking-wider"
                  data-testid={`quick-value-${v}`}
                >
                  R$ {v}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCents(0)}
                className="rounded-full px-3.5 py-1.5 text-xs mono uppercase tracking-wider text-[var(--muted)] hover:text-white transition"
                data-testid="quick-clear"
              >
                limpar
              </button>
            </div>
          )}
          {lockAmount && (
            <p className="text-[10px] mono uppercase tracking-[0.22em] text-[var(--muted)] mt-3 flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-[var(--neon)]" />
              valor fixo de cobrança
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || valueBRL <= 0}
          className="neon-btn w-full rounded-2xl py-4 text-base sm:text-lg flex items-center justify-center gap-3 uppercase"
          data-testid="generate-pix-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 spin" /> Gerando PIX...
            </>
          ) : (
            <>
              GERAR PIX <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <ul className="text-sm text-[var(--muted)] space-y-2.5 leading-relaxed pt-2">
          <li className="flex gap-3">
            <span className="mono text-[var(--neon)] mt-0.5">01</span>
            <span>Digite o valor que deseja pagar e clique no botão.</span>
          </li>
          <li className="flex gap-3">
            <span className="mono text-[var(--neon)] mt-0.5">02</span>
            <span>Um código PIX único será gerado para seu pedido.</span>
          </li>
          <li className="flex gap-3">
            <span className="mono text-[var(--neon)] mt-0.5">03</span>
            <span>O pagamento é processado instantaneamente pelo sistema.</span>
          </li>
        </ul>

        <div className="flex items-center justify-between pt-4 border-t border-[var(--line)] text-[11px] mono uppercase tracking-[0.18em] text-[var(--muted)]">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-[var(--neon)]" />
            criptografado
          </span>
          <span className="flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5 text-[var(--neon)]" />
            misticpay
          </span>
          <span className="flex items-center gap-1.5">
            <QrCode className="w-3.5 h-3.5 text-[var(--neon)]" />
            pix br
          </span>
        </div>
      </form>
    </section>
  );
}
